import * as pdfjsLib from 'pdfjs-dist';
import { createWorker } from 'tesseract.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

// Below this, a PDF's embedded text layer is considered absent (scanned
// paper invoice) rather than just sparse, and OCR is used instead.
const MIN_TEXT_LAYER_LENGTH = 40;

// Invoice summary figures always appear within the first few pages; capping
// this bounds OCR time on unexpectedly long scans.
const MAX_OCR_PAGES = 6;

function parseLocalizedNumber(raw) {
  if (!raw) return null;

  const cleaned = String(raw)
    .replace(/'/g, '')
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');

  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

async function extractPdfText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const pageTexts = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();

    // Content-stream order tracks reading order closely enough for these
    // invoices; reconstructing lines from x/y coordinates instead scrambles
    // multi-column layouts (payment slips, side-by-side tables) far worse.
    pageTexts.push(content.items.map(item => item.str).join(' '));
  }

  return pageTexts.join('\n');
}

async function renderPageToCanvas(page, scale = 2) {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: canvas.getContext('2d'),
    viewport,
  }).promise;

  return canvas;
}

async function ocrPdfText(file, { onStatusChange } = {}) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const worker = await createWorker('deu');

  try {
    const pageTexts = [];
    const pageCount = Math.min(pdf.numPages, MAX_OCR_PAGES);

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      onStatusChange?.(`ocr:${pageNumber}/${pageCount}`);

      const page = await pdf.getPage(pageNumber);
      const canvas = await renderPageToCanvas(page);
      const { data } = await worker.recognize(canvas);
      pageTexts.push(data.text);

      // Invoice summary figures usually all sit on the same page or two;
      // stop scanning further pages once the numbers we actually need have
      // been found instead of always OCR-ing the whole (often multi-page)
      // document — OCR is the slow path, so this is the main lever to keep
      // it from taking longer than necessary.
      const soFar = parseElectricityInvoiceText(pageTexts.join('\n'));

      if (
        soFar.kwh != null &&
        soFar.chf != null &&
        soFar.periodStart &&
        soFar.periodEnd
      ) break;
    }

    return pageTexts.join('\n');
  } finally {
    await worker.terminate();
  }
}

// The capture must start with a digit, not just any char from [\d'.,] — that
// class also contains '.' and ',', which otherwise let the regex latch onto
// a stray period from an abbreviation (e.g. "inkl.") before ever reaching
// the real number, since \D matches '.' too and the search is non-greedy.
const NUMBER = `(\\d[\\d'.,]*)`;


const TOTAL_KWH_PATTERNS = [
  new RegExp(`total\\s*verbrauch\\D{0,25}?${NUMBER}`, 'i'),
  new RegExp(`gesamtverbrauch\\D{0,25}?${NUMBER}`, 'i'),
  new RegExp(`lieferung\\s*total\\D{0,25}?${NUMBER}`, 'i'),
  new RegExp(`\\btotal\\b\\D{0,15}?${NUMBER}\\s*kwh`, 'i'),
  // Municipal Gemeindewerk invoices (e.g. EW Wohlenschwil) don't print a
  // "Total ... kWh" line; the SDL (Systemdienstleistungen) fee row uses the
  // full-period consumption as its base and is the most reliable anchor.
  new RegExp(`\\bsdl\\D{0,25}?${NUMBER}\\s*kwh`, 'i'),
  new RegExp(`stromverbrauch\\D{0,25}?${NUMBER}\\s*kwh`, 'i'),
  new RegExp(`\\bverbrauch\\s*(?:ht|nt)\\D{0,25}?${NUMBER}\\s*kwh`, 'i'),
];



function findTotalKwh(text) {
  for (const pattern of TOTAL_KWH_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      const value = parseLocalizedNumber(match[1]);
      if (value != null && value > 0) return value;
    }
  }

  // Rows list a date range before the value, so a plain "next number" match
  // would grab a day/month/year fragment instead — anchor on the number
  // immediately preceding the "kWh" unit at the end of the row.
  const hochtarif = text.match(
    new RegExp(`verbrauch\\s*hochtarif[\\s\\S]*?${NUMBER}\\s*kwh`, 'i')
  );
  const niedertarif = text.match(
    new RegExp(`verbrauch\\s*niedertarif[\\s\\S]*?${NUMBER}\\s*kwh`, 'i')
  );

  if (hochtarif || niedertarif) {
    const sum =
      (hochtarif ? parseLocalizedNumber(hochtarif[1]) : 0) +
      (niedertarif ? parseLocalizedNumber(niedertarif[1]) : 0);

    if (sum > 0) return sum;
  }

  return null;
}


function findRechnungsbetrag(text) {
  // Explizit als exkl. MWST ausgewiesene Beträge bevorzugen.
  const exclusivePatterns = [
    new RegExp(
      `(?:rechnungsbetrag|gesamtbetrag|total|zwischentotal)` +
      `\\s*(?:in\\s*chf\\s*)?(?:exkl\\.?|ohne)` +
      `\\s*(?:mwst\\.?|mehrwertsteuer)?\\D{0,25}?${NUMBER}`,
      'gi'
    ),
    new RegExp(
      `(?:exkl\\.?\\s*(?:mwst\\.?|mehrwertsteuer))` +
      `\\D{0,25}?${NUMBER}`,
      'gi'
    ),
  ];

  for (const pattern of exclusivePatterns) {
    const matches = [...text.matchAll(pattern)];

    for (const match of matches) {
      const value = parseLocalizedNumber(match[1]);

      if (value != null && value >= 0) {
        return value;
      }
    }
  }

  // Zwischentotal vor MWST und Akontozahlungen suchen.
  const subtotalPattern = new RegExp(
    `\\bzwischentotal\\b\\D{0,25}?${NUMBER}`,
    'gi'
  );

  const subtotals = [...text.matchAll(subtotalPattern)];

  for (const match of subtotals) {
    const value = parseLocalizedNumber(match[1]);
    if (value == null || value < 0) continue;

    const followingText = text.slice(
      match.index + match[0].length,
      match.index + match[0].length + 160
    );

    const vatPosition = followingText.search(
      /\b(?:mwst|mehrwertsteuer)\b/i
    );

    const advancePosition = followingText.search(
      /\b(?:akonto|anzahlung|bereits bezahlt)\b/i
    );

    if (
      vatPosition !== -1 &&
      (advancePosition === -1 || vatPosition < advancePosition)
    ) {
      return value;
    }
  }

  // Bei unklaren Beträgen keine automatische Übernahme.
  return null;
}

const PERIOD_KEYWORDS = [
  'abrechnungsperiode',
  'periode',
  'bezugszeit',
  'rechnung für stromlieferung',
];

const DATE_RANGE_PATTERN =
  /(\d{2}\.\d{2}\.\d{4})\s*(?:-|bis)\s*(\d{2}\.\d{2}\.\d{4})/i;

function findPeriod(text) {
  const lowerText = text.toLowerCase();

  for (const keyword of PERIOD_KEYWORDS) {
    const index = lowerText.indexOf(keyword);
    if (index === -1) continue;

    const window = text.slice(index, index + keyword.length + 60);
    const match = window.match(DATE_RANGE_PATTERN);

    if (match) return { start: match[1], end: match[2] };
  }

  const fallback = text.match(DATE_RANGE_PATTERN);
  if (fallback) return { start: fallback[1], end: fallback[2] };

  return null;
}

export function parseElectricityInvoiceText(text) {
  const kwh = findTotalKwh(text);
  const chf = findRechnungsbetrag(text);
  const period = findPeriod(text);


  return {
    kwh,
    chf,
    chfNeedsReview: chf == null,
    periodStart: period?.start ?? null,
    periodEnd: period?.end ?? null,
    foundAny: kwh != null || chf != null || period != null,
  };
}

export async function parseElectricityInvoicePdf(file, { onStatusChange } = {}) {
  if (!file) {
    throw new Error('Bitte wählen Sie eine PDF-Datei aus.');
  }

  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension !== 'pdf') {
    throw new Error('Die automatische Erkennung funktioniert nur für PDF-Dateien.');
  }

  let text;

  try {
    text = await extractPdfText(file);
  } catch {
    throw new Error('Die PDF-Datei konnte nicht gelesen werden.');
  }

  let usedOcr = false;

  if (text.trim().length < MIN_TEXT_LAYER_LENGTH) {
    usedOcr = true;

    try {
      text = await ocrPdfText(file, { onStatusChange });
    } catch {
      throw new Error(
        'Diese Rechnung enthält keine lesbare Text-Ebene und die Texterkennung (OCR) ist fehlgeschlagen. Bitte manuell erfassen.'
      );
    }
  }

  const result = parseElectricityInvoiceText(text);
  result.usedOcr = usedOcr;

  if (!result.foundAny) {
    throw new Error(
      usedOcr
        ? 'Auch mit Texterkennung (OCR) konnten keine Werte sicher erkannt werden. Bitte manuell erfassen.'
        : 'Die automatische Erkennung funktioniert noch nicht für diesen Rechnungstyp. Bitte geben Sie die Daten manuell ein.'
    );
  }

  return result;
}
